'use client';

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';

import { useDonate } from '../donate-context';
import { DonateErrorObject, DonateVariants } from '../donate.types';
import { useDonateValidation } from '../hooks/use-donate-validation';

const DonateErrorAlert: React.FC<{ message: string | DonateErrorObject }> = ({
  message,
}) => {
  const { t } = useTranslation(['common']);

  if (typeof message === 'string') {
    return (
      <Alert key="validation-error" variant="negative" message={message} />
    );
  }

  const titleNode =
    message.title && t(message.title.key, message.title.payload);

  const bodyNode = message.message && (
    <Trans
      i18nKey={message.message.key}
      values={{ ...message.message.payload }}
      components={{
        anchor: (
          <Link
            className="link"
            href={`mailto:${t('contact.emailGiftPlanning')}`}
          >
            {t('contact.emailGiftPlanning')}
          </Link>
        ),
      }}
    />
  );

  return (
    <Alert
      key="validation-error"
      variant="negative"
      title={titleNode}
      message={bodyNode}
    />
  );
};

export const DonateSubmitButton: React.FC<{
  className?: string;
  alt?: boolean;
}> = ({ className, alt }) => {
  const { t } = useTranslation(['donations']);
  const { state, variant } = useDonate();
  const { getCurrentAmount, validationState } = useDonateValidation();

  const donateAmount = getCurrentAmount() || '';
  const numericAmount = Number(donateAmount);

  const getButtonText = () => {
    if (state.otherSelected && numericAmount <= 0) {
      return t('donations:enterAnAmount');
    }
    if (alt) {
      return t('donations:donate');
    }
    if (variant === DonateVariants.grid && !state.otherSelected) {
      return t('donations:addToBasket');
    }
    if (Number.isNaN(numericAmount)) {
      return t('skippy.donation.donate');
    }

    return t('donations:donateAmount', {
      context: state.donateFrequency,
      amount: numericAmount,
    });
  };

  return (
    <div className={`DonateSubmitButton grid gap-snug-xl ${className || ''}`}>
      <ButtonStyle
        variant="primary"
        size="lg"
        fullWidth
        disabled={validationState.isDisabled || state.isSubmitting}
        isBusy={state.isSubmitting}
      >
        <button
          type="submit"
          onClick={() => {
            trackClick(getButtonText());
          }}
        >
          {getButtonText()}
        </button>
      </ButtonStyle>

      {validationState.message && donateAmount && (
        <DonateErrorAlert message={validationState.message} />
      )}
    </div>
  );
};
